<!-- ----------------------------------------------------------------------------------------------------- -->
<!-- Nothing renders until the shell for this URL is known — see LayoutComponent.shellReady. Each     -->
<!-- layout below owns its own <router-outlet>, so committing to one too early and switching after   -->
<!-- would destroy and re-create the routed component.                                               -->
<!-- ----------------------------------------------------------------------------------------------------- -->
@if (shellReady) {
  <!-- ----------------------------------------------------------------------------------------------------- -->
  <!-- Empty layout -->
  <!-- ----------------------------------------------------------------------------------------------------- -->
  @if (layout === 'empty') {
    <empty-layout></empty-layout>
  }

  <!-- ----------------------------------------------------------------------------------------------------- -->
  <!-- Onboarding shell (top bar + onboarding surface, no navigation) -->
  <!-- ----------------------------------------------------------------------------------------------------- -->
  @if (layout === 'onboarding') {
    <onboarding-layout></onboarding-layout>
  }

  <!-- ----------------------------------------------------------------------------------------------------- -->
  <!-- Layouts with vertical navigation -->
  <!-- ----------------------------------------------------------------------------------------------------- -->

  <!-- Classy -->
  @if (layout === 'classy') {
    <classy-layout></classy-layout>
  }

  <!-- Modern -->
  @if (layout === 'modern') {
    <modern-layout></modern-layout>
  }

  <!-- ----------------------------------------------------------------------------------------------------- -->
  <!-- App-wide "Aan de slag" panel.                                                                    -->
  <!--                                                                                                  -->
  <!-- Mounted here, AFTER the shell switch, so it is a sibling of the layout instead of a child of a   -->
  <!-- routed component: it survives every navigation. The widget- and route-map drawers live inside    -->
  <!-- ModulesComponent and are therefore torn down on each route change — deliberately not copied.     -->
  <!--                                                                                                  -->
  <!-- floatingConfig() is undefined on the dashboard (the banner already covers it), in the           -->
  <!-- empty/onboarding shells, for onboarding accounts, and whenever no floating widget is visible —   -->
  <!-- so nothing at all is rendered in those cases.                                                    -->
  <!-- ----------------------------------------------------------------------------------------------------- -->
  @if (floatingConfig(); as config) {
    <!-- `layout > *` in layout.component.scss forces position/display/flex/width on every direct
         child, so `fixed`, `flex-none` and `w-auto` are here to beat it (class > element selector).
         z-40: above all page content (max z-10 in the app chrome), below the route-map sidebar's
         z-48 stacking context, the fuse-drawer's 300 (which is what the widget drawer now stacks
         at, since the overlay shell dropped that host's own z-48), the loading bar's 999 and the CDK
         overlay container's 1000 — so dialogs, menus and drawers always win. -->
    <!-- floatingLift(): the table's bulk-actions bar is fixed to the bottom edge and would bury
         this panel, so the panel rides up with it. A transform rather than a `bottom` override
         keeps the responsive bottom-4/sm:bottom-6 resting place intact and gives the move a
         cheap, composited transition; max-height shrinks by the same amount so a tall panel
         cannot grow past the top of the viewport once lifted.
         The X half of that same transform carries the widget-drawer content inset (2026-08-25):
         this panel is right-anchored and would otherwise sit under an open panel. It goes through
         the transform rather than a `right` rule precisely BECAUSE this binding already owns the
         element's transform — a second channel would be overwritten by it. `var()` resolves inside
         an inline style value, so the pixel width still never enters TypeScript. Note the slide
         runs at this element's own duration-200, not the drawer's 400ms. -->
    <div
      class="fixed bottom-4 right-4 z-40 flex w-auto flex-none justify-end overflow-y-auto transition-transform duration-200 sm:bottom-6 sm:right-6"
      [style.transform]="'translate(calc(-1 * var(--dd-drawer-inset, 0px)), -' + floatingLift() + 'px)'"
      [style.max-height]="'calc(100dvh - 3rem - ' + floatingLift() + 'px)'"
      data-cy="getting-started-floating">
      <app-getting-started-panel
        mode="floating"
        [config]="config"
        [steps]="floatingSteps()"
        [collapsed]="floatingCollapsed()"
        (toggle)="onToggleGettingStarted()"
        (stepStart)="gettingStarted.start(floatingWidget(), $event)"
        (stepSkip)="gettingStarted.skip(floatingWidget(), $event)"
        (dismiss)="gettingStarted.dismiss(floatingWidget())"></app-getting-started-panel>
    </div>
  }
}
